import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormatPipe,
  MessageBarStore,
  SOFTLINE_FEATURE_MESSAGE_BAR } from '@softline/ui-core';
import { BehaviorSubject, map, Subscription } from 'rxjs';
import { ReactiveFormsModule } from '@angular/forms';
import {
  BackNavigable,
  BackNavigationService,
  CommandStore,
  HeaderComponent,
  showRequestErrors,
  SOFTLINE_FEATURE_COMMANDS,
  SOFTLINE_FEATURE_TITLE,
  StepHeaderComponent,
  TitleStore,
  UploadFileCommand } from '@softline/application';
import {
  SOFTLINE_CONFIG_LLR_KOMMISSIONIERUNG_STEPS,
  SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
  WarenzugangBuchenComponent } from '@softapps/llr/kommisionierung';
import { LlrKommissionierungStore } from '../../store/kommissionierung.store';
import { DateService, Store } from '@softline/core';
import { Router, RouterModule } from '@angular/router';
import { Kommissionierung } from '../../types/kommissionierung';
import { PreviousRouteService } from '../../services/previous-url-service';
import { RemoteConfigService } from '../../services/remote-config.service';

import { RepeatDirective } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
  selector: 'soft-warenzugang-abschliessen',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    HeaderComponent,
    StepHeaderComponent,
    RouterModule,
    WarenzugangBuchenComponent,
    RepeatDirective,
    TranslatePipe],
  templateUrl: './warenzugang-abschliessen.component.html',
  styleUrls: ['./warenzugang-abschliessen.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [FormatPipe] })
export class WarenzugangAbschliessenComponent
  implements OnInit, OnDestroy, BackNavigable
{
  private kommissionierung?: Kommissionierung;
  private kommissionierungSubscription?: Subscription;
  private abschlussArt?: 'LF' | 'BS';
  private archiveKey?: string;

  public warenzugangBuchenClicked$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  public allePositionenAbgeschlossen: boolean = false;

  readonly eingabeLieferscheinnummerPflicht =
    this.remoteConfigService.config.eingabeLieferscheinnummerPflicht;
  readonly paletteEinzelEingabe =
    this.remoteConfigService.config.paletteEinzelEingabe;
  readonly steps = SOFTLINE_CONFIG_LLR_KOMMISSIONIERUNG_STEPS;

  readonly kommissionierung$ = this.store.observe(
    SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
    LlrKommissionierungStore.getters.data
  );

  readonly kommissionierungBewegungen$ = this.kommissionierung$.pipe(
    map((komm) => {
      return komm?.bewebskomms;
    })
  );

  readonly loading$ = this.store.observe(
    SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
    LlrKommissionierungStore.getters.loading
  );

  readonly formatPipe = inject(FormatPipe);

  constructor(
    private store: Store,
    private router: Router,
    private backNavigationService: BackNavigationService,
    private previousRouteService: PreviousRouteService,
    private cdRef: ChangeDetectorRef,
    private remoteConfigService: RemoteConfigService,
    private dateService: DateService
  ) {}

  async ngOnInit(): Promise<void> {
    this.backNavigationService.set(this);

    this.kommissionierungSubscription = this.kommissionierung$.subscribe(
      (komm) => {
        this.kommissionierung = komm;
        this.archiveKey = komm?.archiveKey;
      }
    );



    this.store.commit(
      SOFTLINE_FEATURE_TITLE,
      TitleStore.mutations.setSubTitle,
      `${this.formatPipe.transform(
        this.kommissionierung?.belegdatum,
        'DATE.DEFAULT'
      )}, ${this.kommissionierung?.lieferant.kurzbez} - ${
        this.kommissionierung?.lieferant.nummer
      }`
    );

    if (
      !this.kommissionierung?.bewebskomms.some(
        (bewe) => !bewe?.kommissionierDaten?.abgeschlossen
      )
    )
      this.allePositionenAbgeschlossen = true;

    const uploadFileCommand = new UploadFileCommand(this.store, {
      sources: 'photo',
      selectionMode: 'multi',
      archiveKey: this.archiveKey });

    uploadFileCommand.icon = 'fa-regular fa-camera';
    uploadFileCommand.name =
      '#LLR_KOMMISSIONIERUNG.AKTIONEN.LABEL_FOTOS_HOCHLADEN';

    this.store.commit(
      SOFTLINE_FEATURE_COMMANDS,
      CommandStore.mutations.addSet,
      {
        name: WarenzugangAbschliessenComponent,
        commands: [uploadFileCommand] }
    );
  }

  ngOnDestroy(): void {
    this.store.commit(
      SOFTLINE_FEATURE_TITLE,
      TitleStore.mutations.setSubTitle,
      ''
    );

    this.store.commit(
      SOFTLINE_FEATURE_COMMANDS,
      CommandStore.mutations.removeSet,
      WarenzugangAbschliessenComponent
    );

    if (
      this.kommissionierungSubscription &&
      !this.kommissionierungSubscription.closed
    )
      this.kommissionierungSubscription.unsubscribe();

    this.backNavigationService.set(undefined);
  }

  async navigateBack(): Promise<void> {
    if (this.warenzugangBuchenClicked$.getValue() === true) {
      this.warenzugangBuchenClicked$.next(false);
      return;
    }
    await this.router.navigateByUrl(this.previousRouteService.getPreviousUrl());
  }

  warenzugangBuchenLaden(abschlussArt: 'LF' | 'BS') {
    this.warenzugangBuchenClicked$.next(true);
    this.abschlussArt = abschlussArt;
  }

  async warenzugangBuchen(kommissionierung: Kommissionierung) {
    const action =
      this.abschlussArt === 'LF'
        ? LlrKommissionierungStore.actions.kommissionierungAbschliessenLf
        : LlrKommissionierungStore.actions.kommissionierungAbschliessenBs;

    const message =
      this.abschlussArt === 'BS'
        ? '#LLR_KOMMISSIONIERUNG.DIALOG_GLOBAL.MESSAGE_BESTELLUNG_ABGESCHLOSSEN'
        : '#LLR_KOMMISSIONIERUNG.DIALOG_GLOBAL.MESSAGE_LIEFERSCHEIN_ABGESCHLOSSEN';

    try {
      await this.store.dispatch(SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG, action, {
        idkopfbskomm: kommissionierung.id + '',
        kommissionierung });
    } catch (e) {
      showRequestErrors(this.store, e);
    }

    await this.store.dispatch(
      SOFTLINE_FEATURE_MESSAGE_BAR,
      MessageBarStore.actions.success,
      {
        title:
          '#LLR_KOMMISSIONIERUNG.DIALOG_GLOBAL.TITEL_WARENZUGANG_ABGESCHLOSSEN',
        message: message }
    );
    await this.router.navigate(['llr-bskomm/bestellungen']);
  }
}
